import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, BookOpen, Search, ArrowRight } from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const { data: products } = useQuery({
    queryKey: ["global-search-products", debouncedQuery],
    enabled: debouncedQuery.length >= 2,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const q = debouncedQuery.replace(/[,%()]/g, " ").trim();
      const { data } = await supabase
        .from("products")
        .select("id, slug, name, category, reference")
        .eq("active", true)
        .or(
          [
            `name.ilike.%${q}%`,
            `description.ilike.%${q}%`,
            `reference.ilike.%${q}%`,
            `brand.ilike.%${q}%`,
            `material.ilike.%${q}%`,
          ].join(",")
        )
        .limit(20);
      return data || [];
    },
  });

  const { data: posts } = useQuery({
    queryKey: ["global-search-posts", debouncedQuery],
    enabled: debouncedQuery.length >= 2,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data } = await supabase
        .from("posts")
        .select("id, title, slug")
        .eq("published", true)
        .or(`title.ilike.%${debouncedQuery}%,excerpt.ilike.%${debouncedQuery}%`)
        .limit(4);
      return data || [];
    },
  });

  const handleSelect = useCallback(
    (href: string) => {
      setOpen(false);
      setQuery("");
      navigate(href);
    },
    [navigate]
  );

  const showEmpty =
    debouncedQuery.length >= 2 &&
    (products?.length ?? 0) === 0 &&
    (posts?.length ?? 0) === 0;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder="Buscar produtos, artigos..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {showEmpty && (
          <CommandEmpty>Nenhum resultado para "{debouncedQuery}".</CommandEmpty>
        )}

        {debouncedQuery.length < 2 && (
          <CommandGroup heading="Atalhos rápidos">
            <CommandItem onSelect={() => handleSelect("/catalogo")}>
              <Package className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Catálogo de produtos
              <span className="ml-auto text-xs text-muted-foreground">→</span>
            </CommandItem>
            <CommandItem onSelect={() => handleSelect("/simulador-publico")}>
              <Search className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Simulador de lentes
              <span className="ml-auto text-xs text-muted-foreground">→</span>
            </CommandItem>
            <CommandItem onSelect={() => handleSelect("/blog")}>
              <BookOpen className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Blog
              <span className="ml-auto text-xs text-muted-foreground">→</span>
            </CommandItem>
            <CommandItem onSelect={() => handleSelect(`/catalogo?busca=${encodeURIComponent(query || "")}`)}>
              <ArrowRight className="mr-2 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Ver todos os produtos
              <span className="ml-auto text-xs text-muted-foreground">→</span>
            </CommandItem>
          </CommandGroup>
        )}

        {(products?.length ?? 0) > 0 && (
          <>
            <CommandGroup heading="Produtos">
              {products!.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`produto-${p.id}-${p.name}`}
                  onSelect={() => handleSelect(`/produto/${(p as any).slug ?? p.id}`)}
                >
                  <Package className="mr-2 h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                  <span className="truncate">{p.name}</span>
                  {p.category && (
                    <span className="ml-auto text-xs text-muted-foreground shrink-0 pl-2">{p.category}</span>
                  )}
                </CommandItem>
              ))}
              <CommandItem onSelect={() => handleSelect(`/catalogo?busca=${encodeURIComponent(debouncedQuery)}`)}>
                <Search className="mr-2 h-4 w-4 text-accent shrink-0" aria-hidden="true" />
                <span className="text-accent font-medium">Ver todos os resultados para "{debouncedQuery}"</span>
              </CommandItem>
            </CommandGroup>
          </>
        )}

        {(products?.length ?? 0) > 0 && (posts?.length ?? 0) > 0 && (
          <CommandSeparator />
        )}

        {(posts?.length ?? 0) > 0 && (
          <CommandGroup heading="Artigos do Blog">
            {posts!.map((post) => (
              <CommandItem
                key={post.id}
                value={`post-${post.id}-${post.title}`}
                onSelect={() => handleSelect(`/blog/${post.slug}`)}
              >
                <BookOpen className="mr-2 h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <span className="truncate">{post.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
